import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import DashboardLayout from '../../../../commons/DashboardLayout/DashboardLayout';
import { useConsultationSummaryENT } from '../../../../../hooks/fetchers/useConsultationSummaryENT/useConsultationSummaryENT';
import Button from '../../../../designSystem/Button/Button';
import { ButtonColor, ButtonSize } from '../../../../designSystem/Button/Button.types';
import { ChangeEvent, useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  SEND_DEVIS_QUERY_KEY,
  downloadDevisENT,
  sendDevisQuery,
  updateDevisQuery,
} from './DashboardHistoryENTSummary.query';
import Loading from '../../../../commons/Loading/Loading';
import { formatRemainingTime } from '../../../../../utils/helpers/date/formatRemainingTime';
import Empty from '../../../../commons/Empty/Empty';
import { useMergedStatesFromFetchers } from '../../../../../hooks/useMergedStatesFromFetchers/useMergedStatesFromFetchers';
import { downloadAttachment } from '../../CreateConsultation/CreateConsultForthStep/CreateConsultForthStep.query';

export default () => {
  const { consultationId } = useParams();

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const {
    data,
    isLoading: isConsultationSummaryLoading,
    error: consultationSummaryError,
  } = useConsultationSummaryENT(parseInt(consultationId as string), {
    enabled: Boolean(consultationId),
  });

  const inputRef = useRef<HTMLInputElement>(null);

  const {
    mutate,
    isLoading: isDevisUploading,
    // @ts-ignore
    isInitialLoading,
  } = useMutation<Awaited<any>, Error, FormData>((data) => sendDevisQuery(data), {
    mutationKey: SEND_DEVIS_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: () => {
      navigate(0);
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const { mutate: uploadNewDevis, isLoading: isNewDevisUploading } = useMutation<
    Awaited<any>,
    Error,
    FormData
  >((data) => updateDevisQuery(data), {
    mutationKey: SEND_DEVIS_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: () => {
      navigate(0);
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const onFileChangeCapture = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (data) {
        // @ts-ignore
        const { files } = e.target;
        const formData = new FormData();
        formData.append('consultationId', consultationId as string);
        formData.append('consultationMatchId', data.infos.id_match.toString() || '');
        if (files) {
          // @ts-ignore
          for (const file of files) {
            formData.append('files', file);
          }
          if (data.devis) {
            uploadNewDevis(formData);
          } else {
            mutate(formData);
          }
        }
      } else {
        alert('Impossible de déposer un devis actuellement. Veuillez réessayer plus tard.');
      }
    },
    [data, consultationId]
  );

  const onClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const getContentByStatus = useCallback(() => {
    if (
      moment().isBetween(data?.infos.start, moment(data?.infos.end).add(18, 'hours')) &&
      data?.infos.id_step === 4
    ) {
      switch (data?.infos.status) {
        case 1:
          return (
            <>
              <input
                ref={inputRef}
                type="file"
                multiple
                hidden
                onChangeCapture={onFileChangeCapture}
              />
              <Button
                style={{
                  alignSelf: 'unset',
                  textAlign: 'center',
                  borderRadius: 6,
                  width: '100%',
                  paddingLeft: 0,
                  paddingRight: 0,
                }}
                onClick={(e) => {
                  e.preventDefault();
                  onClick();
                }}
                size={ButtonSize.Large}
                disabled={false}>
                Déposer un devis
              </Button>
            </>
          );
        case 2:
          return (
            <>
              <input
                ref={inputRef}
                type="file"
                multiple
                hidden
                onChangeCapture={onFileChangeCapture}
              />
              <Button
                style={{
                  alignSelf: 'unset',
                  textAlign: 'center',
                  borderRadius: 6,
                  width: '100%',
                  paddingLeft: 0,
                  paddingRight: 0,
                }}
                onClick={(e) => {
                  e.preventDefault();
                  onClick();
                }}
                size={ButtonSize.Large}
                disabled={false}>
                Remplacer mon devis
              </Button>
            </>
          );
        // case 2:
        //   return (
        //     <Button
        //       style={{
        //         alignSelf: 'unset',
        //         textAlign: 'center',
        //         borderRadius: 6,
        //         width: '100%',
        //         paddingLeft: 0,
        //         paddingRight: 0,
        //       }}
        //       onClick={(e) => {
        //         e.preventDefault();
        //         onClick()
        //       }}
        //       size={ButtonSize.Large}
        //       disabled={true}>
        //       Devis déjà envoyé
        //     </Button>
        //   )
        default:
          return null;
      }
    } else {
      return (
        <Button
          style={{
            alignSelf: 'unset',
            textAlign: 'center',
            borderRadius: 6,
            width: '100%',
            paddingLeft: 0,
            paddingRight: 0,
          }}
          size={ButtonSize.Large}
          disabled={true}>
          Consultation inactive
        </Button>
      );
    }
  }, [inputRef, data]);

  const { isLoading, error } = useMergedStatesFromFetchers({
    loadings: [
      isConsultationSummaryLoading,
      isDevisUploading,
      isInitialLoading,
      isNewDevisUploading,
    ],
    errors: [consultationSummaryError],
  });

  return (
    <>
      <Helmet>
        <title>Résumé de la consultation #{consultationId}</title>
      </Helmet>
      <DashboardLayout style={{ backgroundColor: 'var(--gray-1)' }}>
        {error ? (
          <Empty>{error.message}</Empty>
        ) : isLoading ? (
          <Loading />
        ) : (
          <>
            <div style={{ display: 'flex', flex: 1, alignSelf: 'normal' }}>
              <div className="dashboard-history">
                <div className="dashboard-history__item">
                  <div className="dashboard-history__item-row">
                    <div className="dashboard-history__item-content">
                      <label className="dashboard-history__item-text-1">
                        Titre de la consultation
                      </label>
                      <span className="dashboard-history__item-text-3">{data?.infos.title}</span>
                    </div>
                    {data?.infos.ref && (
                      <div className="createConsultForthStep__item-content">
                        <label className="createConsultForthStep__item-text-1">Référence</label>
                        <span className="createConsultForthStep__item-text-2">
                          {data?.infos.ref}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="dashboard-history__item-row">
                    <div className="dashboard-history__item-content">
                      <label className="dashboard-history__item-text-1">Type de prestation</label>
                      <span className="dashboard-history__item-text-2">{data?.infos.type}</span>
                    </div>
                    <div className="dashboard-history__item-content">
                      <label className="dashboard-history__item-text-1">Domaine d'activité</label>
                      <span className="dashboard-history__item-text-2">
                        {data?.infos.fieldOfActivity}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="dashboard-history__item dashboard-history__item--right">
                  <div className="dashboard-history__item-row">
                    <div className="dashboard-history__item-content">
                      <label className="dashboard-history__item-text-1">Début de l'offre</label>
                      <span className="dashboard-history__item-text-4">
                        {moment(data?.infos.start).format('dddd DD MMMM YYYY')}
                      </span>
                    </div>
                    <div className="dashboard-history__item-content">
                      <label className="dashboard-history__item-text-1">
                        Durée de la consultation
                      </label>
                      <span className="dashboard-history__item-text-2">
                        {moment(data?.infos.end).diff(data?.infos.start, 'days')} jours
                      </span>
                    </div>
                  </div>
                  <div className="dashboard-history__item-row">
                    <div className="dashboard-history__item-content">
                      <label className="dashboard-history__item-text-1">Fin de l'offre</label>
                      <span className="dashboard-history__item-text-4">
                        {moment(data?.infos.end).format('dddd DD MMMM YYYY')}
                      </span>
                    </div>
                    {data?.infos.start &&
                      data?.infos.end &&
                      (moment().isBefore(data.infos.start) ? (
                        <div className="dashboard-history__item-content">
                          <label className="dashboard-history__item-text-1">Débute dans</label>
                          <span className="dashboard-history__item-text-2">
                            {formatRemainingTime(data.infos.start)}
                          </span>
                        </div>
                      ) : (
                        <div className="dashboard-history__item-content">
                          <label className="dashboard-history__item-text-1">Temps restant</label>
                          <span className="dashboard-history__item-text-2">
                            {formatRemainingTime(
                              moment(data?.infos.end)
                                .add(18, 'hours')
                                .toDate()
                            )}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
                {Boolean(data && data?.prestations.length > 0) && (
                  <div className="dashboard-history__item">
                    <div className="dashboard-history__item-row">
                      <div className="dashboard-history__item-content">
                        <label className="dashboard-history__item-text-1">
                          Contenu de la consultation
                        </label>
                        {data?.prestations.map(({ quantity, description }, index) => (
                          <span className="dashboard-history__item-text-2" key={index}>
                            {index === 0 && `\n`}{' '}
                            {quantity && (
                              <>
                                <strong>{quantity}</strong>x
                              </>
                            )}{' '}
                            {description} {`\n\n`}
                          </span>
                        ))}
                        {data?.infos.further_description &&
                          <span className="createConsultForthStep__item-text-2">
                            {data.infos.further_description}
                          </span>
                        }
                      </div>
                    </div>
                  </div>
                )}
                {Boolean(data?.infos.firstName) && (
                  <div className="dashboard-history__item dashboard-history__item--right">
                    <label className="dashboard-history__item-text-5">Personne à contacter</label>
                    <div className="dashboard-history__item-row">
                      <div className="dashboard-history__item-content">
                        <label className="dashboard-history__item-text-1">Nom</label>
                        <span className="dashboard-history__item-text-2">
                          {data?.infos.lastName}
                        </span>
                      </div>
                      <div className="dashboard-history__item-content">
                        <label className="dashboard-history__item-text-1">Prénom</label>
                        <span className="dashboard-history__item-text-2">
                          {data?.infos.firstName}
                        </span>
                      </div>
                    </div>
                    <div className="dashboard-history__item-row">
                      <div className="dashboard-history__item-content">
                        <label className="dashboard-history__item-text-1">Adresse email</label>
                        <span className="dashboard-history__item-text-2">{data?.infos.email}</span>
                      </div>
                      <div className="dashboard-history__item-content">
                        <label className="dashboard-history__item-text-1">
                          Numéro de téléphone
                        </label>
                        <span className="dashboard-history__item-text-2">{data?.infos.phone}</span>
                      </div>
                    </div>
                  </div>
                )}
                {data?.infos.attachments &&
                  <div className="createConsultForthStep__item" style={{ paddingTop: 0 }}>
                    {data.infos.attachments.split(',').map((file, index) => (
                      <Button
                        key={index}
                        style={{
                          alignSelf: 'unset',
                          textAlign: 'center',
                          borderRadius: 6,
                          marginBottom: 'var(--spacing-s)',
                          width: '100%',
                          paddingLeft: 0,
                          paddingRight: 0,
                        }}
                        onClick={(e) => {
                          e.preventDefault()
                          downloadAttachment(file)
                        }}
                      >
                        {`Pièce jointe n°${index + 1}`}
                      </Button>
                    ))}
                  </div>
                }
              </div>
            </div>
            {data?.devis && (
              <div style={{ alignSelf: 'center' }}>
                <p>Devis déposé le {moment(data.devis.devis_deposed_at).format('DD/MM/YYYY')}</p>
                <Button
                  style={{
                    marginBottom: 'var(--spacing-sToM)',
                  }}
                  color={ButtonColor.Secondary}
                  onClick={() =>
                    downloadDevisENT(parseInt(consultationId as string), data.infos.id_match)
                  }>
                  Télécharger mon devis
                </Button>
              </div>
            )}
            <div style={{ marginBottom: 'var(--spacing-s)' }}>
              {getContentByStatus()}
              <Link to={`questions`}>
                <Button
                  style={{
                    alignSelf: 'unset',
                    textAlign: 'center',
                    borderRadius: 6,
                    marginTop: 'var(--spacing-s)',
                    width: '100%',
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}
                  size={ButtonSize.Large}
                  color={ButtonColor.Secondary}
                  disabled={false}>
                  {/* {(data && data.count.questions > 0) ? `Voir ${data.count.questions} questions` : 'Poser une question'} */}
                  Questions / Réponses
                </Button>
              </Link>
            </div>
          </>
        )}
      </DashboardLayout>
    </>
  );
};
